import { Button } from '@/components/ui/button'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatFromUTC } from '@/utils/function'
import { paths } from '@/utils/path'
import {
    Calendar,
    ChevronDown,
    ChevronUp,
    Clock,
    Globe,
    Hash,
    Loader2,
    Mail,
    MapPin,
    Phone,
    Route,
    User,
    UserCheck,
    UserPlus,
    Users,
    Wifi,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, Chip } from 'vnetwork-ui'
import {
    ChatRoomStatusOptions,
    getChatRoomStatusColor,
    getPlatformColor,
} from '../../constants'
import { getRoomJourney } from '../../services/analytics'
import { ChatRoom } from '../../types/response'
import JourneyTimeline from './JourneyTimeline'
import SourceInfo from './SourceInfo'

interface RoomInfoProps {
    room: ChatRoom
    onSyncToMarketing?: () => void
    existingContactId?: number
}

const RoomInfo: React.FC<RoomInfoProps> = ({
    room,
    onSyncToMarketing,
    existingContactId,
}) => {
    const { t } = useTranslation('livechat')
    const navigate = useNavigate()
    const [journeyExpanded, setJourneyExpanded] = useState(false)

    const statusOption = ChatRoomStatusOptions(t).find(
        (option) => option.value === room.status
    )

    const customer = room.users?.find((u) => u.userType === 'user')
    const employees = room.users?.filter((u) => u.userType === 'employee') || []

    // Check if room has source tracking data
    const hasSourceData = !!(room.sourceUrl || room.sourceOrigin)

    // Fetch journey data using useQueryWithHandler - only when expanded
    const {
        data: journey,
        isLoading: journeyLoading,
        isError: journeyError,
        renderState: journeyRenderState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.LIVECHAT_ANALYTICS_JOURNEY, room.id],
        queryFn: () => getRoomJourney(room.id),
        enabled: journeyExpanded && hasSourceData,
    })

    if (journeyError) {
        return <div className="text-red-500">Error fetching journey data</div>
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-gray-50 px-4 py-3 border-b flex-shrink-0 flex items-center min-h-[64px]">
                <h3 className="font-semibold text-gray-700">
                    {t('Room Information')}
                </h3>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Status */}
                <div className="flex gap-2">
                    <Chip
                        label={statusOption?.label || t(room.status)}
                        size="small"
                        variant="contained"
                        color={getChatRoomStatusColor(room.status)}
                    />
                    {room.platform && (
                        <Chip
                            label={
                                room.platform.charAt(0).toUpperCase() +
                                room.platform.slice(1)
                            }
                            size="small"
                            variant="outlined"
                            color={getPlatformColor(room.platform)}
                        />
                    )}
                </div>

                {/* Customer Info */}
                {customer && (
                    <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                            <User size={14} className="text-blue-500" />
                            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                                {t('Customer')}
                            </span>
                        </div>
                        <div className="flex items-start gap-3">
                            {customer.avatar ? (
                                <Avatar
                                    src={customer.avatar}
                                    alt={customer.name}
                                    className="w-10 h-10 flex-shrink-0"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                                    <User size={20} className="text-blue-600" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-800">
                                    {customer.name || '-'}
                                </p>
                                {customer.email && (
                                    <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                                        <Mail
                                            size={12}
                                            className="flex-shrink-0"
                                        />
                                        <span className="break-all">
                                            {customer.email}
                                        </span>
                                    </div>
                                )}
                                {customer.phoneNumber && (
                                    <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                                        <Phone
                                            size={12}
                                            className="flex-shrink-0"
                                        />
                                        <span>{customer.phoneNumber}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Sync to Marketing / View Lead button */}
                {customer && existingContactId ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-green-600 border-green-200 hover:bg-green-50"
                        onClick={() =>
                            navigate(
                                `${paths.customerMkt}?contact_source_id=${existingContactId}`
                            )
                        }
                    >
                        <UserCheck size={14} className="mr-1.5" />
                        {t('view-lead')}
                    </Button>
                ) : customer && onSyncToMarketing ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={onSyncToMarketing}
                    >
                        <UserPlus size={14} className="mr-1.5" />
                        {t('sync-to-marketing')}
                    </Button>
                ) : null}

                {/* User Journey Section */}
                {hasSourceData && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Collapsible Header */}
                        <button
                            onClick={() => setJourneyExpanded(!journeyExpanded)}
                            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <Route size={14} className="text-purple-500" />
                                <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                                    {t('User Journey')}
                                </span>
                                {journey && (
                                    <span className="text-xs text-gray-400">
                                        ({journey.totalPages} {t('pages')})
                                    </span>
                                )}
                            </div>
                            {journeyLoading ? (
                                <Loader2
                                    size={14}
                                    className="text-gray-400 animate-spin"
                                />
                            ) : journeyExpanded ? (
                                <ChevronUp
                                    size={14}
                                    className="text-gray-400"
                                />
                            ) : (
                                <ChevronDown
                                    size={14}
                                    className="text-gray-400"
                                />
                            )}
                        </button>

                        {/* Collapsible Content */}
                        {journeyExpanded && (
                            <div className="p-3 space-y-3 border-t border-gray-200">
                                {journeyRenderState() ||
                                    (journey && (
                                        <>
                                            {/* Source Info */}
                                            <SourceInfo
                                                sourceInfo={journey.sourceInfo}
                                                utmParams={journey.utmParams}
                                            />

                                            {/* Visitor Location Info */}
                                            {journey.pageVisits?.[0]
                                                ?.visitorIp && (
                                                <div className="bg-cyan-50 rounded-lg p-3">
                                                    <div className="flex items-center gap-1.5 text-cyan-600 mb-2">
                                                        <MapPin size={12} />
                                                        <span className="text-xs font-medium">
                                                            {t(
                                                                'Visitor Location'
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        {journey.pageVisits[0]
                                                            .visitorIp && (
                                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                                <Wifi
                                                                    size={12}
                                                                    className="text-cyan-500"
                                                                />
                                                                <span className="font-mono text-xs">
                                                                    {
                                                                        journey
                                                                            .pageVisits[0]
                                                                            .visitorIp
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}
                                                        {(journey.pageVisits[0]
                                                            .visitorCity ||
                                                            journey
                                                                .pageVisits[0]
                                                                .visitorCountry) && (
                                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                                <Globe
                                                                    size={12}
                                                                    className="text-cyan-500"
                                                                />
                                                                <span>
                                                                    {[
                                                                        journey
                                                                            .pageVisits[0]
                                                                            .visitorCity,
                                                                        journey
                                                                            .pageVisits[0]
                                                                            .visitorCountry,
                                                                    ]
                                                                        .filter(
                                                                            Boolean
                                                                        )
                                                                        .join(
                                                                            ', '
                                                                        )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Journey Timeline */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                                <div className="flex items-center gap-1.5 text-gray-500 mb-3">
                                                    <Route size={12} />
                                                    <span className="text-xs">
                                                        {t('Page Visits')}
                                                    </span>
                                                </div>
                                                <JourneyTimeline
                                                    pageVisits={
                                                        journey.pageVisits
                                                    }
                                                    uniqueOrigins={
                                                        journey.uniqueOrigins
                                                    }
                                                />
                                            </div>
                                        </>
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Room ID */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                        <Hash size={12} />
                        <span className="text-xs">{t('Room ID')}</span>
                    </div>
                    <p className="text-sm font-mono text-gray-700 break-all">
                        {room.id}
                    </p>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                            <Calendar size={12} />
                            <span className="text-xs">{t('Created')}</span>
                        </div>
                        <p className="text-sm text-gray-700">
                            {formatFromUTC(room.createdAt, 'DD/MM/YYYY')}
                        </p>
                        <p className="text-xs text-gray-400">
                            {formatFromUTC(room.createdAt, 'HH:mm')}
                        </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                            <Clock size={12} />
                            <span className="text-xs">
                                {t('Last Activity')}
                            </span>
                        </div>
                        <p className="text-sm text-gray-700">
                            {formatFromUTC(room.updatedAt, 'DD/MM/YYYY')}
                        </p>
                        <p className="text-xs text-gray-400">
                            {formatFromUTC(room.updatedAt, 'HH:mm')}
                        </p>
                    </div>
                </div>

                {/* Assignee */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                        <User size={12} />
                        <span className="text-xs">{t('Assignee')}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-700">
                        {room.assignee?.name || t('Unassigned')}
                    </p>
                </div>

                {/* Participants */}
                {employees.length > 0 && (
                    <div>
                        <div className="flex items-center gap-1.5 text-gray-500 mb-2">
                            <Users size={12} />
                            <span className="text-xs">{t('Participants')}</span>
                            <span className="text-xs text-gray-400">
                                ({employees.length})
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {employees.map((emp) => (
                                <span
                                    key={emp.id}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full"
                                >
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                    {emp.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default RoomInfo
